import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ENDPOINTS } from "../config/api";
import VideoPlayer from "../components/VideoPlayer";
import GoLiveModal from "../components/GoLiveModal";
import StartStreamV2 from "../components/modals/StartStreamV2";
import useTranslation from "../hooks/useTranslation";

// Composants stylisés
const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: ${({ theme }) => theme.color.background};
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.color.primary};
  font-size: 2.5rem;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.color.text};
  font-size: 1.1rem;
  opacity: 0.8;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 30px;
  margin-bottom: 30px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  color: ${({ theme }) => theme.color.primary};
  font-size: 1.5rem;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StreamKeySection = styled.div`
  background: ${({ theme }) => theme.color.background};
  border: 2px solid ${({ theme }) => theme.color.primary};
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
`;

const StreamKeyLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.color.text};
  font-weight: bold;
  margin-bottom: 10px;
`;

const StreamKeyInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.color.background};
  color: ${({ theme }) =>
    theme.mode === "dark" ? theme.color.primaryText : theme.color.darkerText};
  font-family: monospace;
  font-size: 14px;
  margin-bottom: 15px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: ${({ theme }) => theme.color.primary};
    color: white;
    
    &:hover {
      background: ${({ theme }) => theme.color.primaryHover};
    }
  }

  &.secondary {
    background: ${({ theme }) => theme.color.secondary};
    color: ${({ theme }) => (theme.dark ? "white" : "white")};
    
    &:hover {
      background: ${({ theme }) => theme.color.secondaryHover};
    }
  }

  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ isLive }) => (isLive ? "#28a745" : "#dc3545")};
`;

const StatusText = styled.span`
  color: ${({ theme }) => theme.color.text};
  font-weight: bold;
`;

const ObsConfig = styled.div`
  background: ${({ theme }) => theme.color.background};
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
`;

const ConfigItem = styled.div`
  margin-bottom: 15px;
`;

const ConfigLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.color.text};
  font-weight: bold;
  margin-bottom: 5px;
`;

const ConfigValue = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  padding: 10px;
  border-radius: 5px;
  font-family: monospace;
  font-size: 14px;
  color: ${({ theme }) => theme.color.text};
  word-break: break-all;
`;

const CopyButton = styled.button`
  background: ${({ theme }) => theme.color.primary};
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 12px;
  cursor: pointer;
  margin-left: 10px;

  &:hover {
    background: ${({ theme }) => theme.color.primaryHover};
  }
`;

const VideoPlayerContainer = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;

  .video-js {
    width: 100%;
    height: 400px;
    border-radius: 10px;
  }

  .vjs-control-bar {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const NoStreamMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  background: ${({ theme }) => theme.color.secondaryBg};
  border-radius: 10px;
  color: ${({ theme }) => theme.color.text};
  font-size: 1.2rem;
  text-align: center;
  flex-direction: column;
  gap: 15px;
`;

const StreamerDashboard = () => {
  const [streamKey, setStreamKey] = useState("");
  const [rtmpUrl, setRtmpUrl] = useState("");
  const [fullUrl, setFullUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [playbackLink, setPlaybackLink] = useState("");
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [showStartStreamV2, setShowStartStreamV2] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [eventCategory, setEventCategory] = useState('');
  const [eventParticipants, setEventParticipants] = useState([]);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const user = useSelector((state) => state.user.data);
  const username = user?.username;
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  // Get eventSlug from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const eventSlug = queryParams.get('eventSlug');

  // Récupérer la clé de stream
  const fetchStreamKey = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(API_ENDPOINTS.INIT_CHANNEL, {
        withCredentials: true,
        timeout: 10000 // ✅ Timeout de 10 secondes
      });

      const {
        streamKey,
        playbackLink,
        injectionLink
      } = response.data;

      // ✅ Validation des données reçues
      if (!streamKey) {
        throw new Error(t("streamerdashboard.stream_key_not_received", "Clé de stream non reçue"));
      }

      setStreamKey(streamKey);
      const rtmp = injectionLink || "";
      setRtmpUrl(rtmp);
      setFullUrl(rtmp && streamKey ? rtmp + streamKey : "");
      setPlaybackLink(playbackLink);

      console.log("✅ Clé de stream récupérée:", { streamKey, rtmpUrl, fullUrl, playbackLink });
    } catch (error) {
      console.error("Erreur lors de la récupération de la clé de stream:", error);

      let errorMessage = t("streamerdashboard.impossible_de_recuperer", "Impossible de récupérer la clé de stream");
      if (error.response?.status === 401) {
        errorMessage = t("streamerdashboard.session_expiree_veuillez", "Session expirée, veuillez vous reconnecter");
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = t("streamerdashboard.delai_d_attente", "Délai d'attente dépassé, vérifiez votre connexion");
      } else if (error.response?.status >= 500) {
        errorMessage = t("streamerdashboard.erreur_serveur_veuillez", "Erreur serveur, veuillez réessayer plus tard");
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Régénérer la clé de stream
  const regenerateStreamKey = async () => {
    try {
      const response = await axios.post(
        API_ENDPOINTS.MY_STREAM_KEY + "/regenerate",
        {},
        { withCredentials: true }
      );

      const { streamKey, rtmpUrl, fullUrl } = response.data;
      setStreamKey(streamKey);
      setRtmpUrl(rtmpUrl);
      setFullUrl(fullUrl);
      toast.success(t("streamerdashboard.cle_de_stream", "Clé de stream régénérée avec succès"));
    } catch (error) {
      console.error("Erreur lors de la régénération:", error);
      toast.error(t("streamerdashboard.impossible_de_regenerer", "Impossible de régénérer la clé de stream"));
    }
  };

  // Copier dans le presse-papiers
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(t("streamerdashboard.copie_dans_le", "Copié dans le presse-papiers"));
    });
  };

  // Vérifier le statut du stream
  const checkStreamStatus = async () => {
    try {
      // ✅ Source de vérité: l'endpoint organizer "mychannel/init" (retourne le Channel Prisma)
      const initRes = await axios.get(API_ENDPOINTS.INIT_CHANNEL, {
        withCredentials: true,
        timeout: 5000, // ✅ Timeout plus court pour les vérifications de statut
      });

      const channel = initRes.data || {};
      const live = channel.isLive === true;
      const currentPlaybackLink = channel.playbackLink || "";

      setIsLive(live);

      // ✅ Mettre à jour le playbackLink si disponible
      if (currentPlaybackLink && currentPlaybackLink !== playbackLink) {
        setPlaybackLink(currentPlaybackLink);
        console.log("✅ Nouveau playbackLink détecté:", currentPlaybackLink);
      }

      console.log("✅ Statut du stream vérifié:", {
        isLive: live,
        hasPlaybackLink: !!currentPlaybackLink,
      });
    } catch (error) {
      console.error("Erreur lors de la vérification du statut:", error);
      setIsLive(false);
    }
  };

  // Handle Go Live with stream configuration
  const handleGoLive = async (streamConfig) => {
    try {
      // Update channel with stream configuration
      await axios.post(
        `${API_ENDPOINTS.STREAM}update-stream-config`,
        {
          title: streamConfig.title,
          gameId: streamConfig.gameId,
          tags: streamConfig.tags,
          enableRestream: streamConfig.enableRestream,
          eventId: streamConfig.eventId
        },
        { withCredentials: true }
      );

      toast.success(
        t(
          "streamerdashboard.config_updated",
          "Configuration du stream mise à jour ! Vous pouvez maintenant passer en direct dans OBS."
        )
      );

      // Refresh stream status
      checkStreamStatus();
    } catch (error) {
      console.error("Error updating stream config:", error);
      throw error;
    }
  };

  // Load event + participants if an eventSlug is provided
  useEffect(() => {
    const fetchEventAndParticipants = async () => {
      if (!eventSlug) return;
      try {
        const eventRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL || ''}/api/events/events/${eventSlug}`,
          { withCredentials: true }
        );
        const ev = eventRes.data.event;

        // Security: only the organizer can manage this event in StreamerDashboard
        if (ev.createdBy?.toString() !== user?.id?.toString()) {
          toast.error('You are not authorized to manage this event');
          history.push(`/events/${eventSlug}`);
          return;
        }

        setEventData(ev);
        setEventCategory(ev?.category?.slug || '');

        const partsRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL || ''}/api/events/participants/${ev.id}`,
          { withCredentials: true }
        );
        setEventParticipants(partsRes.data.participants || []);
      } catch (err) {
        console.error('Error fetching event or participants for StreamerDashboard:', err);
      }
    };

    fetchEventAndParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSlug]);

  const uploadEventBanner = async (file) => {
    if (!eventSlug || !file) return;
    try {
      setUploadingBanner(true);
      const fd = new FormData();
      fd.append('filename', file);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || ''}/api/events/events/${eventSlug}/banner`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Banner updated');
      setEventData((prev) => prev ? ({ ...prev, bannerImage: res.data?.bannerImage || prev.bannerImage }) : prev);
    } catch (err) {
      console.error('Error uploading banner:', err);
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingBanner(false);
    }
  };

  // Charger les données au montage
  useEffect(() => {
    fetchStreamKey();
    checkStreamStatus();

    // Vérifier le statut toutes les 30 secondes
    const interval = setInterval(checkStreamStatus, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  if (isLoading) {
    return (
      <DashboardContainer>
        <div>{t("common.loading", "Chargement...")}</div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>🎥 {t("streamerdashboard.dashboard_title", "Dashboard Streamer")}</Title>
        <Subtitle>{t("streamerdashboard.gerez_votre_stream", "Gérez votre stream et configurez OBS")}</Subtitle>
      </Header>

      <Grid>
        {/* Carte 1: Clé de Stream */}
        <Card>
          <CardTitle>🔑 {t("streamerdashboard.stream_key_title", "Clé de Stream")}</CardTitle>

          <StreamKeySection>
            <StatusIndicator style={{ marginBottom: 14 }}>
              <StatusDot isLive={isLive} />
              <StatusText>
                {isLive ? `🔴 ${t("streamerdashboard.live", "EN DIRECT")}` : `⚫ ${t("streamerdashboard.offline", "HORS LIGNE")}`}
              </StatusText>
            </StatusIndicator>

            <StreamKeyLabel>{t("streamerdashboard.current_stream_key", "Clé de stream actuelle")}</StreamKeyLabel>
            <StreamKeyInput
              type="text"
              value={streamKey}
              readOnly
              placeholder={t("streamerdashboard.stream_key_placeholder", "Clé de stream")}
            />

            <ButtonGroup>
              <Button
                onClick={() => setShowStartStreamV2(true)}
                style={{ background: '#667eea' }}
              >
                🚀 Go Live V2
              </Button>
              <Button
                className="danger"
                onClick={regenerateStreamKey}
              >
                🔄 {t("streamerdashboard.regenerate_key", "Régénérer la clé")}
              </Button>
            </ButtonGroup>
          </StreamKeySection>

          <ObsConfig>
            <h3>⚙️ {t("streamerdashboard.obs_config", "Configuration OBS")}</h3>

            <ConfigItem>
              <ConfigLabel>{t("streamerdashboard.server_rtmp", "Server RTMP :")}</ConfigLabel>
              <ConfigValue>
                {rtmpUrl}
                <CopyButton onClick={() => copyToClipboard(rtmpUrl)}>
                  {t("common.copy", "Copier")}
                </CopyButton>
              </ConfigValue>
            </ConfigItem>

            <ConfigItem>
              <ConfigLabel>{t("streamerdashboard.votre_cle_de", "Votre clé de stream :")}</ConfigLabel>
              <ConfigValue>
                {streamKey}
                <CopyButton onClick={() => copyToClipboard(streamKey)}>
                  {t("common.copy", "Copier")}
                </CopyButton>
              </ConfigValue>
            </ConfigItem>

            <ConfigItem>
              <ConfigLabel>{t("streamerdashboard.url_complete_", "URL complète :")}</ConfigLabel>
              <ConfigValue>
                {fullUrl}
                <CopyButton onClick={() => copyToClipboard(fullUrl)}>
                  {t("common.copy", "Copier")}
                </CopyButton>
              </ConfigValue>
            </ConfigItem>
          </ObsConfig>
        </Card>
      </Grid>

      {/* Event participants (for Stream & Workshop events linked to this dashboard) */}
      {eventData && (eventCategory === 'stream' || eventCategory === 'workshop') && (
        <Card style={{ marginTop: '20px' }}>
          <CardTitle>
            🖼️ Event Banner
          </CardTitle>
          <p style={{ color: '#9CA3AF', marginTop: '-6px' }}>
            This banner is shown on the event details page and event lists.
          </p>
          {eventData?.bannerImage ? (
            <div style={{ marginTop: 10, marginBottom: 10 }}>
              <img
                src={eventData.bannerImage}
                alt="Event banner"
                style={{ width: '100%', maxWidth: 720, borderRadius: 12, border: '1px solid #374151' }}
              />
            </div>
          ) : (
            <p style={{ color: '#9CA3AF' }}>No banner uploaded yet.</p>
          )}
          <input
            type="file"
            accept="image/*"
            disabled={uploadingBanner}
            onChange={(e) => uploadEventBanner(e.target.files?.[0])}
          />
        </Card>
      )}

      {/* Event spectators/access list (stream/workshop) */}
      {eventData && (eventCategory === 'stream' || eventCategory === 'workshop') && (
        <Card style={{ marginTop: '20px' }}>
          <CardTitle>
            👥 Spectators ({eventParticipants.length})
          </CardTitle>
          {eventParticipants.length === 0 ? (
            <p style={{ color: '#9CA3AF' }}>
              No participants registered yet.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #374151' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #374151' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #374151' }}>Access</th>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #374151' }}>Registered At</th>
                </tr>
              </thead>
              <tbody>
                {eventParticipants.map((p) => (
                  <tr key={p.id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #111827' }}>
                      {p.user?.name || p.user?.username || 'Unknown'}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #111827', textTransform: 'capitalize' }}>
                      {p.status}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #111827', fontSize: '0.85rem' }}>
                      {(() => {
                        const price = Number(eventData?.ticketPrice || 0);
                        if (!price) return 'Free';
                        return p.ticketId ? 'Paid (ticket)' : 'No ticket';
                      })()}
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #111827', fontSize: '0.85rem', color: '#9CA3AF' }}>
                      {p.registeredAt ? new Date(p.registeredAt).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Guide rapide OBS section removed as requested */}

      {/* Player de vidéo */}
      <VideoPlayerContainer>
        <CardTitle>📺 {t("streamerdashboard.stream_preview", "Aperçu du Stream")}</CardTitle>
        {isLive ? (
          <VideoPlayer
            type="hls"
            id="streamer-dashboard-player"
            src={playbackLink} // <-- Utilise l'URL IVS ici
            isLive={true}
            isMute={false}
            autoPlay={true}
          />
        ) : (
          <NoStreamMessage>
            <div>📺</div>
            <div>{t("streamerdashboard.aucun_stream_en", "Aucun stream en cours")}</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>
              {t("streamerdashboard.start_obs_to_see", "Démarrez votre stream dans OBS pour le voir ici")}
            </div>
          </NoStreamMessage>
        )}
        <div style={{ marginTop: '15px', textAlign: 'right' }}>
          <Button
            onClick={() => window.open(`/stream/${username}`, '_blank')}
            className="primary"
          >
            🔗 {t("streamerdashboard.open_stream_page", "Ouvrir ma page stream")}
          </Button>
        </div>
      </VideoPlayerContainer>

      {/* Section Chat DynamoDB - removed (non functional endpoints) */}

      {/* Go Live Modal */}
      <GoLiveModal
        isOpen={showGoLiveModal}
        onClose={() => setShowGoLiveModal(false)}
        eventSlug={eventSlug}
        onGoLive={handleGoLive}
      />

      {/* Start Stream V2 Modal (TEST) */}
      {showStartStreamV2 && (
        <ModalOverlay onClick={() => setShowStartStreamV2(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <StartStreamV2
              eventSlug={eventSlug}
              hideModal={() => setShowStartStreamV2(false)}
              serverIP={rtmpUrl}
              streamKey={streamKey}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};

export default StreamerDashboard;

// Modal Styled Components for V2
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.color.background};
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`; 